import React, { useState, useEffect } from 'react';
import { getToken } from '../utils/storage';
import CustomerLayout from '../components/CustomerLayout';
import CollapsibleTable from '../components/CollapsibleTable';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, Edit, X, Eye, EyeOff, Monitor } from 'lucide-react';
import confirm from '../utils/confirm';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerArticles = () => {
  const { t } = useTranslation();
  const [articles, setArticles] = useState([]);
  const [screens, setScreens] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedScreens, setSelectedScreens] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    start_date: new Date().toISOString().slice(0, 16), // YYYY-MM-DDTHH:MM
    end_date: '',
    image_url: ''
  });

  useEffect(() => {
    fetchArticles();
    fetchScreens();
  }, []);

  const fetchArticles = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/articles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArticles(response.data);
    } catch (error) {
      toast.error(t('customerArticles.errorLoadingArticles'));
    }
  };

  const fetchScreens = async () => {
    try {
      const token = getToken();
      const response = await axios.get(`${API}/screens`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setScreens(response.data);
    } catch (error) {
      console.error('Could not fetch screens:', error);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteImage = async () => {
    if (!editingArticle || !editingArticle.image_url) {
      // Just clear preview if no saved image
      setImagePreview(null);
      setSelectedImage(null);
      return;
    }

    if (!await confirm.danger(t('customerArticles.confirmDeleteImage'))) {
      return;
    }

    try {
      const token = getToken();
      await axios.delete(`${API}/articles/${editingArticle.id}/image`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(t('customerArticles.imageDeleted'));
      setImagePreview(null);
      setSelectedImage(null);
      setFormData({...formData, image_url: ''});
      
      // Update article in state
      setArticles(articles.map(a => 
        a.id === editingArticle.id ? {...a, image_url: ''} : a
      ));
      setEditingArticle({...editingArticle, image_url: ''});
    } catch (error) {
      toast.error(t('customerArticles.errorDeletingImage'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title || !formData.title.trim()) {
      toast.error(t('customerArticles.titleRequired'));
      return;
    }
    if (!formData.content || !formData.content.trim()) {
      toast.error(t('customerArticles.contentRequired'));
      return;
    }
    if (!formData.end_date || !formData.end_date.trim()) {
      toast.error(t('customerArticles.endDateRequired'));
      return;
    }
    
    try {
      const token = getToken();
      
      // Upload image first if selected (uses separate endpoint for article images)
      let imageUrl = formData.image_url || '';
      if (selectedImage) {
        const imageFormData = new FormData();
        imageFormData.append('file', selectedImage);
        
        const uploadResponse = await axios.post(`${API}/articles/upload-image`, imageFormData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        imageUrl = uploadResponse.data.image_url;
      }
      
      const articleData = { 
        ...formData, 
        image_url: imageUrl,
        screen_ids: selectedScreens  // Empty array means all screens
      };
      
      if (editingArticle) {
        await axios.put(`${API}/articles/${editingArticle.id}`, articleData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('customerArticles.articleUpdated'));
      } else {
        await axios.post(`${API}/articles`, articleData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('customerArticles.articleCreated'));
      }
      
      handleCloseModal();
      fetchArticles();
      setFormData({
        title: '',
        content: '',
        start_date: new Date().toISOString().slice(0, 16), // YYYY-MM-DDTHH:MM
        end_date: '',
        image_url: ''
      });
    } catch (error) {
      console.error('Save error:', error);
      if (error.response?.status === 404) {
        toast.error(t('customerArticles.articleNotFound'));
        handleCloseModal();
        fetchArticles(); // Refresh list to remove stale articles
      } else {
        toast.error(t('customerArticles.errorSavingArticle'));
      }
    }
  };

  const handleCloseModal = () => {
    // Revoke blob URL if it exists
    if (imagePreview && imagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreview);
    }
    setShowModal(false);
    setEditingArticle(null);
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleNewArticle = () => {
    setEditingArticle(null);
    setSelectedImage(null);
    setImagePreview(null);
    setSelectedScreens([]);  // Empty = all screens
    setFormData({
      title: '',
      content: '',
      start_date: new Date().toISOString().slice(0, 16), // YYYY-MM-DDTHH:MM
      end_date: '',
      image_url: ''
    });
    setShowModal(true);
  };

  const toggleVisibility = async (articleId, currentVisibility) => {
    try {
      const token = getToken();
      await axios.patch(`${API}/articles/${articleId}/toggle-visibility`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(currentVisibility ? t('customerArticles.articleHidden') : t('customerArticles.articleShown'));
      fetchArticles();
    } catch (error) {
      console.error('Toggle visibility error:', error);
      toast.error(t('customerArticles.errorTogglingVisibility'));
    }
  };

  const handleEdit = async (article) => {
    setEditingArticle(article);
    // Format datetime for datetime-local input (YYYY-MM-DDTHH:MM)
    const formatDateTime = (isoString) => {
      if (!isoString) return '';
      return isoString.slice(0, 16); // Takes YYYY-MM-DDTHH:MM from ISO string
    };
    
    setSelectedScreens(article.screen_ids || []);
    
    setFormData({
      title: article.title,
      content: article.content,
      start_date: formatDateTime(article.start_date),
      end_date: formatDateTime(article.end_date),
      image_url: article.image_url || ''
    });
    
    // Fetch image with authorization and create blob URL
    if (article.image_url) {
      try {
        // Check if it's a Cloudinary URL (starts with http/https)
        if (article.image_url.startsWith('http')) {
          // Direct Cloudinary URL - no need to fetch with auth
          setImagePreview(article.image_url);
        } else {
          // Local file - fetch with auth
          const token = getToken();
          const response = await axios.get(`${BACKEND_URL}${article.image_url}`, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          });
          const blobUrl = URL.createObjectURL(response.data);
          setImagePreview(blobUrl);
        }
      } catch (error) {
        console.error('Failed to load image:', error);
        setImagePreview(null);
      }
    }
    setShowModal(true);
  };

  const handleDelete = async (articleId) => {
    if (!await confirm.danger(t('customerArticles.confirmDeleteArticle'))) return;
    
    try {
      const token = getToken();
      await axios.delete(`${API}/articles/${articleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('customerArticles.articleDeleted'));
      fetchArticles();
    } catch (error) {
      toast.error(t('customerArticles.errorDeletingArticle'));
    }
  };

  return (
    <CustomerLayout>
      <div className="admin-header">
        <h1>{t('customerArticles.title')}</h1>
        <button className="btn btn-primary" onClick={handleNewArticle}>
          <Plus size={20} /> {t('customerArticles.newArticle')}
        </button>
      </div>

      <CollapsibleTable
        columns={[
          {
            key: 'title',
            label: t('customerArticles.tableTitle'),
            minWidth: '150px'
          },
          {
            key: 'content',
            label: t('customerArticles.tableContent'),
            minWidth: '200px',
            render: (article) => (
              <div style={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {article.content}
              </div>
            )
          },
          {
            key: 'start_date',
            label: t('customerArticles.tableStartDate'),
            minWidth: '100px',
            render: (article) => new Date(article.start_date).toLocaleDateString('no-NO')
          },
          {
            key: 'end_date',
            label: t('customerArticles.tableEndDate'),
            minWidth: '100px',
            render: (article) => new Date(article.end_date).toLocaleDateString('no-NO')
          }
        ]}
        data={articles}
        onRowDoubleClick={(article) => handleEdit(article)}
        actions={(article) => (
          <>
            <button
              className={`btn ${article.is_visible !== false ? 'btn-secondary' : 'btn-outline'}`}
              onClick={() => toggleVisibility(article.id, article.is_visible !== false)}
              style={{ padding: 'clamp(0.4rem, 1vw, 0.5rem)' }}
              title={article.is_visible !== false ? t('customerArticles.hideArticle') : t('customerArticles.showArticle')}
            >
              {article.is_visible !== false ? (
                <Eye size={window.innerWidth < 1280 ? 14 : 16} />
              ) : (
                <EyeOff size={window.innerWidth < 1280 ? 14 : 16} />
              )}
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handleEdit(article)}
              style={{ padding: 'clamp(0.4rem, 1vw, 0.5rem)' }}
            >
              <Edit size={window.innerWidth < 1280 ? 14 : 16} />
            </button>
            <button
              className="btn btn-danger"
              onClick={() => handleDelete(article.id)}
              style={{ padding: 'clamp(0.4rem, 1vw, 0.5rem)' }}
            >
              <Trash2 size={window.innerWidth < 1280 ? 14 : 16} />
            </button>
          </>
        )}
      />

      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingArticle ? t('customerArticles.editArticleTitle') : t('customerArticles.newArticleTitle')}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>{t('customerArticles.formTitle')}</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>{t('customerArticles.formContent')}</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  rows={6}
                  required
                />
              </div>

              <div className="form-group">
                <label>{t('customerArticles.formImage')}</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                />
                {imagePreview && (
                  <div style={{ marginTop: '1rem', position: 'relative', display: 'inline-block' }}>
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      style={{ 
                        maxWidth: '200px', 
                        maxHeight: '200px', 
                        objectFit: 'cover',
                        borderRadius: '8px'
                      }} 
                    />
                    <button
                      type="button"
                      onClick={handleDeleteImage}
                      style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        background: '#ef4444',
                        border: 'none',
                        borderRadius: '50%',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        color: 'white'
                      }}
                      title={t('customerArticles.deleteImage')}
                    >
                      <X size={18} />
                    </button>
                  </div>
                )}
                <small style={{ display: 'block', marginTop: '0.5rem', opacity: 0.7, color: 'white' }}>
                  {t('customerArticles.imageResizeInfo')}
                </small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>{t('customerArticles.formStartDate')}</label>
                  <input
                    type="datetime-local"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>{t('customerArticles.formEndDate')}</label>
                  <input
                    type="datetime-local"
                    value={formData.end_date}
                    onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    required
                  />
                </div>
              </div>

              {/* Screen Selection */}
              <div className="form-group" style={{ marginTop: '1.5rem' }}>
                <label style={{ marginBottom: '0.75rem', display: 'block', color: 'white' }}>
                  {t('customerArticles.showOnScreens')} {selectedScreens.length === 0 && <span style={{ opacity: 0.6, color: 'white' }}>({t('customerArticles.allScreens')})</span>}
                </label>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                  gap: '0.75rem',
                  padding: '1rem',
                  background: 'rgba(80, 227, 194, 0.05)',
                  borderRadius: '8px',
                  border: '1px solid rgba(80, 227, 194, 0.2)'
                }}>
                  {screens.map(screen => (
                    <label 
                      key={screen.id} 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.5rem', 
                        cursor: 'pointer',
                        padding: '0.5rem',
                        borderRadius: '4px',
                        background: selectedScreens.includes(screen.id) ? 'rgba(80, 227, 194, 0.15)' : 'transparent',
                        transition: 'background 0.2s',
                        color: 'white'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedScreens.includes(screen.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedScreens([...selectedScreens, screen.id]);
                          } else {
                            setSelectedScreens(selectedScreens.filter(id => id !== screen.id));
                          }
                        }}
                        style={{ width: 'auto' }}
                      />
                      <Monitor 
                        size={16} 
                        style={{ 
                          transform: screen.orientation === 'portrait' ? 'rotate(90deg)' : 'none',
                          color: '#50E3C2'
                        }} 
                      />
                      <span style={{ fontSize: '0.9rem', color: 'white' }}>{screen.screen_name}</span>
                    </label>
                  ))}
                </div>
                <small style={{ display: 'block', marginTop: '0.5rem', opacity: 0.7, color: 'white' }}>
                  {t('customerArticles.screenSelectionHelp')}
                </small>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button type="submit" className="btn btn-primary">
                  {editingArticle ? t('customerArticles.update') : t('customerArticles.create')}
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>{t('customerArticles.cancel')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </CustomerLayout>
  );
};

export default CustomerArticles;
